import { useState, useEffect } from 'react';
import TransferHeader from './TransferHeader';
import AccountItem from './AccountItem';
import CurrencyInputbox from './CurrencyInputbox';
import ExchangeRate from './ExchangeRate';
import NormalButton from './NormalButton';

import NotePencilIcon from '../svg/NotePencilIcon';

import UsFlag from '../../assets/images/country/us.png';

const getEstimatedTime = (country, paymentRoute) => {
  if (country === 'México') return { time: '1-2 horas', method: 'SPEI' };
  if (country === 'Europa') return { time: 'Menos de 30 minutos', method: 'SEPA' };
  if (country === 'Estados Unidos' && paymentRoute === 'ACH') return { time: '1-2 días hábiles', method: 'ACH' };
  if (country === 'Estados Unidos' && paymentRoute === 'WIRE') return { time: 'Mismo día', method: 'WIRE' };
  if (country === 'Colombia') return { time: 'Mismo día (1-6 horas)', method: 'ACH' };
  if (country === 'Brasil') return { time: 'Minutos', method: 'PIX' };
  return { time: '1-3 días hábiles', method: 'BIC/SWIFT' };
};

export default function CurrencyConversionForm({
  country,
  title,
  toAccount,
  dollarValue,
  setDollarValue,
  flag,
  localCurrency,
  localCurrencyValue,
  setLocalCurrencyValue,
  exchangeRate,
  setProgress,
  exchangeRate_,
  paymentRoute,
}) {
  const developerFee = parseFloat(import.meta.env.VITE_DEVELOPER_FEE) || 0.5;
  const isACH = paymentRoute === 'ACH';
  const feePercent = isACH ? 0 : developerFee;
  const rate = exchangeRate_ || exchangeRate;

  // Track which field the user is editing
  const [editingField, setEditingField] = useState(null);

  // When user types in local currency → calculate USD
  useEffect(() => {
    if (editingField !== 'local') return;
    const localAmount = parseFloat(localCurrencyValue);
    if (!isNaN(localAmount) && localAmount > 0 && rate > 0) {
      // localAmount in destination currency → convert to USD
      const usdBeforeFee = localAmount / rate;
      const fee = usdBeforeFee * (feePercent / 100);
      const totalUsd = usdBeforeFee + fee;
      setDollarValue(totalUsd.toFixed(2));
    } else {
      setDollarValue('');
    }
  }, [localCurrencyValue, rate, editingField]);

  // When user types in USD → calculate local currency
  useEffect(() => {
    if (editingField !== 'usd') return;
    const usdAmount = parseFloat(dollarValue);
    if (!isNaN(usdAmount) && usdAmount > 0 && rate > 0) {
      // USD amount → subtract fee → convert to local
      const fee = usdAmount * (feePercent / 100);
      const usdAfterFee = usdAmount - fee;
      const localAmount = usdAfterFee * rate;
      setLocalCurrencyValue(localAmount.toFixed(2));
    } else {
      setLocalCurrencyValue('');
    }
  }, [dollarValue, rate, editingField]);

  const handleLocalChange = (val) => {
    setEditingField('local');
    setLocalCurrencyValue(val);
  };

  const handleUsdChange = (val) => {
    setEditingField('usd');
    setDollarValue(val);
  };

  // Calculate fee breakdown for display
  const usdNum = parseFloat(dollarValue) || 0;
  const localNum = parseFloat(localCurrencyValue) || 0;
  let feeAmount = 0;
  let usdBeforeFee = 0;
  let recipientGets = localNum;

  if (editingField === 'local' || editingField === null) {
    // User entered local amount → USD was calculated including fee
    usdBeforeFee = rate > 0 ? localNum / rate : 0;
    feeAmount = usdBeforeFee * (feePercent / 100);
  } else {
    // User entered USD → local was calculated after fee
    feeAmount = usdNum * (feePercent / 100);
    usdBeforeFee = usdNum - feeAmount;
    recipientGets = usdBeforeFee * rate;
  }

  const processImageURI = (uri) => {
    if (!uri) return null;

    let baseURL = import.meta.env.VITE_IMAGE_URL;
    const replacedUri = uri.replace('/src', baseURL);

    if (replacedUri.startsWith('http://') || replacedUri.startsWith('https://')) {
      return { uri: replacedUri };
    }

    const fullUri = `${baseURL}${replacedUri}`;
    return { uri: fullUri };
  };

  let account;
  if (country == 'Estados Unidos' || country == 'México' || country == 'Europa' || country == 'Brasil') {
    account = {
      bank_image: processImageURI(toAccount?.external_account?.bank_image).uri,
      account_owner_name: toAccount.external_account.account_owner_name,
      bank_name: toAccount.external_account.bank_name,
      account_number:
        toAccount.destination_currency == 'usd'
          ? toAccount.external_account.account.account_number
          : toAccount.destination_currency == 'eur'
          ? toAccount.external_account.iban.account_number
          : toAccount.destination_currency == 'brl'
          ? toAccount.external_account.pix?.key || ''
          : toAccount.external_account.clabe.account_number,
    };
  } else if (country == 'Colombia') {
    account = {
      bank_image: processImageURI(toAccount.bank_image).uri,
      account_owner_name: toAccount.first_name + ' ' + toAccount.last_name,
      bank_name: toAccount.bank_name,
      account_number: toAccount.bank_account_number,
    };
  } else {
    account = {};
  }

  const formatNum = (n) => {
    if (isNaN(n) || n === 0) return '0.00';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <>
      <TransferHeader
        headerTitle={title}
        description="Ingresa el monto que deseas transferir"
        goBack={() => setProgress(0)}
      />

      <AccountItem account={account} endIcon={<NotePencilIcon />} onClick={() => setProgress(0)} />

      <div className="space-y-4">
        {/* Recipient gets (local currency) */}
        <CurrencyInputbox
          title="Recibe el destinatario"
          icon={flag}
          currency={localCurrency}
          value={localCurrencyValue}
          onChange={handleLocalChange}
        />
        {/* User pays (USD) */}
        {country !== 'Estados Unidos' && (
          <CurrencyInputbox
            title="Tu envías (USDC)"
            icon={UsFlag}
            currency="USD"
            value={dollarValue}
            onChange={handleUsdChange}
          />
        )}
      </div>

      {country !== 'Estados Unidos' && (
        <div className="space-y-2 mb-2 font-[poppins] text-xs text-[#414651] font-medium leading-[20px]">
          {exchangeRate_ ? (
            <ExchangeRate rate={exchangeRate_} currency="COP" />
          ) : (
            <ExchangeRate rate={exchangeRate} currency={localCurrency} />
          )}
          <p className="text-xs font-medium text-center">
            La cotización se actualizará en <span className="text-[#07276B]">50 segundos</span>
          </p>
        </div>
      )}

      {/* Fee breakdown */}
      <div className="bg-white border border-[#E6EBF7] rounded-[14px] p-4 space-y-2 font-[poppins]">
        <div className="flex justify-between text-xs">
          <span className="text-[#535862]">Monto a convertir</span>
          <span className="text-[#181D27] font-medium">${formatNum(usdBeforeFee)} USD</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[#535862]">
            Comisión FlowexPay ({feePercent}%)
          </span>
          <span className={`font-medium ${feeAmount === 0 ? 'text-[#16A34A]' : 'text-[#181D27]'}`}>
            {feeAmount === 0 ? 'Gratis' : `$${formatNum(feeAmount)} USD`}
          </span>
        </div>
        <div className="border-t border-[#E6EBF7] pt-2 flex justify-between text-xs">
          <span className="text-[#181D27] font-semibold">Total a debitar de tu cuenta</span>
          <span className="text-[#181D27] font-bold">${formatNum(usdNum)} USD</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[#2A59C6] font-semibold">Recibe el destinatario (aprox.)</span>
          <span className="text-[#2A59C6] font-bold">
            {formatNum(editingField === 'usd' ? recipientGets : localNum)} {localCurrency}
          </span>
        </div>
        {country !== 'Estados Unidos' && (
          <p className="text-[10px] text-[#717680] leading-[14px] pt-1">
            * El monto final puede variar ligeramente debido a tasas de conversión aplicadas por el banco intermediario o destino, ajenas a FlowexPay.
          </p>
        )}
      </div>

      {/* Estimated delivery time */}
      <div className="bg-[#F0F4FF] border border-[#E6EBF7] rounded-[12px] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-[#2A59C6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs text-[#535862] font-[poppins]">Tiempo estimado de entrega</span>
        </div>
        <span className="text-xs font-semibold text-[#2A59C6] font-[poppins]">
          {getEstimatedTime(country, paymentRoute).time}
        </span>
      </div>

      <div className="float-right">
        <NormalButton
          customClass="bg-[#092F7B] text-white"
          text="Continuar"
          onClick={() => setProgress(2)}
        />
      </div>
    </>
  );
}
